// client/src/components/pages/LexiStar/StatusBoard/RunDrawer.jsx
//
// Right-hand drawer with one truck's run (LS-09 §5.24), opened from the run
// chip on the status board: GET /runs/:id/summary rendered as a RunStrip. The
// optional `renderNextLoad(summary, reload)` fills the end-of-strip slot with the
// *Next load from here* panel.

import React, { useCallback, useEffect, useState } from 'react';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { getRunSummary } from '../../../../services/lexistarStatusBoard';
import RunStrip from './RunStrip';

export default function RunDrawer({ runId, isOpen, onClose, renderNextLoad, title }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    setError(null);
    try {
      setSummary(await getRunSummary(runId));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load the run');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    if (isOpen) {
      setSummary(null);
      load();
    }
  }, [isOpen, load]);

  if (!isOpen) return null;
  const run = summary?.run;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={run ? `Run ${run.registration}` : title || 'Run'}
        className="h-full w-full max-w-4xl bg-surface shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 truncate">
              {run ? (
                <>
                  <span className="font-mono">{run.registration}</span> · run
                </>
              ) : (
                title || 'Run'
              )}
            </h3>
            {run && (
              <p className="text-sm text-slate-500 mt-0.5">
                {run.driver_name || 'No driver recorded'} · {run.shift_date} ·{' '}
                {run.state === 'closed' ? 'Closed' : 'Open'}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 transition-colors mt-1"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading && !summary && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-slate-200 border-t-primary-500"></div>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2 text-sm">
              <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}
          {summary && <RunStrip summary={summary}>{renderNextLoad?.(summary, load)}</RunStrip>}
        </div>
      </aside>
    </div>
  );
}
